// Google Analytics 4 wiring (lazy, env-driven, SPA-safe).
//
// • Reads the Measurement ID from `REACT_APP_GA_MEASUREMENT_ID`.
//   If it's missing or empty, everything no-ops — no requests fly,
//   no globals are set, no console warnings. Lets the codebase ship
//   to environments where the user hasn't filled in their GA ID yet
//   without crashing or polluting the network tab.
// • The script tag is injected at runtime (once) instead of in
//   index.html so the same build works for users who flip the ID
//   on/off in `.env` without rebuilding.
// • `trackPageview` is wired into the React Router from App.js so
//   SPA route changes show up as separate pageviews (without this,
//   GA only ever sees the very first page load).
//
// What gets tracked (zero-config from us, GA auto-tracks):
//   – Pageviews + route changes
//   – Scrolls past 90%
//   – Outbound link clicks
//   – File downloads
//   – Site search (?q= params)
//   – Video engagement on embedded YouTube iframes
//
// Where users land in GA:
//   GA4 → Reports → Realtime / Reports → Engagement → Pages and screens

const MEASUREMENT_ID = (process.env.REACT_APP_GA_MEASUREMENT_ID || "").trim();
let initialized = false;

function isEnabled() {
  return !!MEASUREMENT_ID && MEASUREMENT_ID.startsWith("G-");
}

export function initAnalytics() {
  if (initialized || !isEnabled()) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  initialized = true;

  // 1) Load the gtag.js loader.
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
  document.head.appendChild(s);

  // 2) Bootstrap the global gtag function + dataLayer.
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag("js", new Date());
  // `send_page_view: false` — we manually fire pageviews from the
  // router so we control the timing (after the new route's <title>
  // and search params have settled).
  gtag("config", MEASUREMENT_ID, { send_page_view: false });
}

// Called by the router watcher in App.js whenever `location` changes.
export function trackPageview(path) {
  if (!isEnabled() || typeof window === "undefined" || !window.gtag) return;
  const page_path = path || window.location.pathname + window.location.search;
  window.gtag("event", "page_view", {
    page_path,
    page_location: window.location.href,
    page_title: document?.title,
    send_to: MEASUREMENT_ID,
  });
}

// Custom event tracking — for buttons/flows that matter to the
// business (e.g. "started_devotional", "joined_room", "donated").
// Safe no-op when GA is disabled.
export function trackEvent(name, params = {}) {
  if (!isEnabled() || typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", name, { ...params, send_to: MEASUREMENT_ID });
}

export const analyticsEnabled = isEnabled;
